import React, { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CategoryIcon from '@mui/icons-material/Category';
import { useParams } from 'react-router';
import { getPermissionsList } from 'Permission/Domain/Services/GetPermissionsListService';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { getPermissionGroup } from 'PermissionGroup/Domain/Services/GetPermissionGroupService';
import { createPermissionGroup } from 'PermissionGroup/Domain/Services/CreatePermissionGroupService';
import { patchPermissionGroup } from 'PermissionGroup/Domain/Services/PatchPermissionGroupService';
import { PRIVATE_ADMIN_LIST_PERMISSIONGROUPS } from '../Constants/PathsConstant';
import { useTranslation } from 'react-i18next';

function FormPermissionGroupView() {
  const { t } = useTranslation();
  const { id } = useParams();

  const [sku, setSku] = useState('');
  const [description, setDescription] = useState('');

  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [getPermissions, setGetPermissions] = useState(true);

  const [sendingApiRequest, setSendingApiRequest] = useState(false);

  useEffect(() => {
    if (id !== "new") {
      setSendingApiRequest(true);
      getPermissionGroup(id).then((response) => {
        setSku(response.sku || '');
        setDescription(response.description || '');
        if (response.permissions !== null && response.permissions.length > 0) {
          var perms = [];
          response.permissions.forEach((permission) => {
            if (permission && permission.sku) perms.push(permission.sku);
          });
          setSelectedPermissions(perms);
        }
        setSendingApiRequest(false);
      });
    }
  }, [id]);

  useEffect(() => {
    if (getPermissions) {
      setSendingApiRequest(true);
      getPermissionsList({ limit: 1000 }).then((response) => {
        setPermissions(response.records || []);
        setGetPermissions(false);
        setSendingApiRequest(false);
      });
    }
  }, [getPermissions]);

  const handleSkuChange = (event) => setSku(event.target.value);
  const handleDescriptionChange = (event) => setDescription(event.target.value);

  const handlePermissionChange = (event) => {
    const value = event.target.value;
    const currentIndex = selectedPermissions.indexOf(value);
    const newSelectedPermissions = [...selectedPermissions];
    if (currentIndex === -1) {
      newSelectedPermissions.push(value);
    } else {
      newSelectedPermissions.splice(currentIndex, 1);
    }
    setSelectedPermissions(newSelectedPermissions);
  };

  // Group permissions by category
  const permissionsByCategory = useMemo(() => {
    const grouped = {};
    permissions.forEach((perm) => {
      const cat = perm.category || 'GENERAL';
      if (!grouped[cat]) {
        grouped[cat] = [];
      }
      grouped[cat].push(perm);
    });
    return grouped;
  }, [permissions]);

  const handleToggleCategory = (catPermissions) => {
    const catSkus = catPermissions.map((p) => p.sku);
    const allSelected = catSkus.every((s) => selectedPermissions.includes(s));
    if (allSelected) {
      // Unselect all in this category
      setSelectedPermissions(selectedPermissions.filter((s) => !catSkus.includes(s)));
    } else {
      // Select all in this category
      const toAdd = catSkus.filter((s) => !selectedPermissions.includes(s));
      setSelectedPermissions([...selectedPermissions, ...toAdd]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      var apiResponse = null;
      if (sendingApiRequest) return;
      setSendingApiRequest(true);
      if (id === "new") {
        apiResponse = await createPermissionGroup({
          sku: sku,
          description: description,
          permissions: selectedPermissions
        });
      } else {
        apiResponse = await patchPermissionGroup(id, {
          sku: sku,
          description: description,
          permissions: selectedPermissions
        });
      }
      setSendingApiRequest(false);
      if (apiResponse !== null) {
        window.location.href = process.env.PUBLIC_URL + PRIVATE_ADMIN_LIST_PERMISSIONGROUPS;
      }
    } catch (error) {
      //console.error(error);
    }
  };

  return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', pb: 6 }}>
        <h1>{id === "new" ? t('permissiongroup.form.create') : t('permissiongroup.form.update')}</h1>
        <form onSubmit={handleSubmit} style={{ width: '65%', maxWidth: '900px' }}>
          <TextField
            style={{ width: '100%' }}
            required
            label={t('permissiongroup.form.sku')}
            variant="outlined"
            autoComplete='off'
            value={sku}
            onChange={handleSkuChange}
          />
          <br /><br />
          <TextField
            style={{ width: '100%' }}
            required
            label={t('permissiongroup.form.description')}
            variant="outlined"
            autoComplete='off'
            value={description}
            onChange={handleDescriptionChange}
          />
          <br /><br />
          <FormControl component="fieldset" style={{ width: '100%' }}>
            {permissions.length === 0 ? (
              <p>{t('permissiongroup.form.permissions_not_found')}</p>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('permissiongroup.form.permissions')}
                  </Typography>
                  <Chip
                    label={`${selectedPermissions.length} de ${permissions.length} seleccionados`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Los permisos están agrupados por categoría para facilitar su configuración.
                </Typography>
              </Box>
            )}

            {Object.keys(permissionsByCategory).map((catName) => {
              const catPerms = permissionsByCategory[catName];
              const selectedCount = catPerms.filter((p) => selectedPermissions.includes(p.sku)).length;
              const allSelected = selectedCount === catPerms.length;

              return (
                <Accordion key={catName} defaultExpanded sx={{ mb: 1.5, border: '1px solid #e0e0e0', boxShadow: 'none', '&:before': { display: 'none' }, borderRadius: '8px !important' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <CategoryIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {catName}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${selectedCount} / ${catPerms.length}`}
                          color={selectedCount > 0 ? (allSelected ? 'success' : 'primary') : 'default'}
                          variant={selectedCount > 0 ? 'filled' : 'outlined'}
                        />
                      </Stack>
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleCategory(catPerms);
                        }}
                        sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                      >
                        {allSelected ? 'Desmarcar todos' : 'Marcar todos'}
                      </Button>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2, px: 3, bgcolor: '#fafafa', borderTop: '1px solid #f0f0f0' }}>
                    {catPerms.map((permission) => (
                      <FormGroup key={permission.sku}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(permission.sku)}
                              onChange={handlePermissionChange}
                              value={permission.sku}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{permission.description}</span>
                              <Typography variant="caption" sx={{ color: 'text.secondary', bgcolor: '#eee', px: 0.8, py: 0.2, borderRadius: 1 }}>
                                {permission.sku}
                              </Typography>
                            </Box>
                          }
                        />
                      </FormGroup>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </FormControl>
          <br /><br />
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="contained" color="primary" type="submit" size="large">
              {id === "new" ? t('permissiongroup.form.create') : t('permissiongroup.form.update')}
            </Button>
            <Button variant="contained" color="error" href={(process.env.PUBLIC_URL + PRIVATE_ADMIN_LIST_PERMISSIONGROUPS)} size="large">
              {t('permissiongroup.form.cancel')}
            </Button>
          </Box>
        </form>
        <DialogLoader open={sendingApiRequest} />
      </Box>
  );
}

export default FormPermissionGroupView;
